import type { Step } from 'react-joyride';
import type { StepAction } from './interaction';
import {
  byText,
  card,
  drawer,
  drawerOpen,
  field,
  findByText,
  noDrawer,
  runsDrawerOpen,
} from './dom';

/**
 * How-to guide of a page or drawer: starts the first time it applies (after the welcome tour),
 * then again from the « ? » button.
 */
export interface Guide {
  id: string;
  /** Whether the guide applies to what is on screen (checked every second). */
  appliesTo: (pathname: string) => boolean;
  /** Drawer guides start on their own only: the « ? » button stays under drawers. */
  inDrawer?: boolean;
  steps: Step[];
}

const runsOpen = (pathname: string) =>
  /^\/ephemeral-envs\/runs\/[^/]+/.test(pathname) || runsDrawerOpen();

/**
 * A step the user has to do before moving on (interaction.ts): no dark overlay, so menus and
 * dropdowns the action opens stay visible.
 */
const todo = (step: Step, action: StepAction): Step => ({
  ...step,
  hideOverlay: true,
  data: { ...step.data, action },
});

const intro = (title: string, content: Step['content']): Step => ({
  target: 'body',
  placement: 'center',
  title,
  content,
  data: { primaryLabel: 'Voir comment' },
});

/** A guide starts once its first target is on screen (data loaded), not before. */
export function isReady(guide: Guide): boolean {
  const target = guide.steps[0]?.target;
  if (!target || target === 'body') return true;
  if (typeof target === 'string')
    return Boolean(document.querySelector(target));
  if (typeof target === 'function') return Boolean(target());
  return true;
}

export const GUIDES: Guide[] = [
  {
    id: 'ephemeral-envs',
    appliesTo: pathname =>
      /^\/ephemeral-envs\/?$/.test(pathname) &&
      noDrawer() &&
      !runsOpen(pathname),
    steps: [
      intro(
        'Environnements éphémères',
        <>
          <p>
            Chaque ticket peut avoir son propre Bobbee, déployé depuis sa
            branche avec sa base de données.
          </p>
          <p>
            En trois temps : tu lances l'env, tu suis le pipeline, puis tu
            testes sur <code>mp-1234.feature.bobbee.co</code>.
          </p>
        </>,
      ),
      todo(
        {
          target: '[aria-label="Filtrer les environnements"]',
          title: 'Quels envs afficher',
          content:
            'Les tiens, ceux de ton équipe (squads GitHub) ou tous les envs en direct.',
        },
        { kind: 'click', hint: 'Clique sur un filtre' },
      ),
      {
        target: () =>
          findByText('p, span', 'GitHub:') ?? field('Votre compte GitHub')(),
        title: 'Ton compte GitHub',
        content:
          'Backstage s’en sert pour retrouver tes PR et les envs que tu lances depuis GitHub. À lier une seule fois.',
      },
      {
        target: '[aria-label="Environnements éphémères"]',
        title: 'La liste',
        content:
          'Statut de l’app et de la base, temps restant et dernier run. Clique sur une ligne pour voir le pipeline, les logs et le rapport E2E.',
      },
      {
        target: '[aria-label="Contrôle env"]',
        title: 'Durée de vie',
        content:
          'Prolonge ou raccourcis un env, ou supprime-le quand tu as fini. À l’expiration, il est supprimé automatiquement.',
      },
      {
        target: byText('[role="tab"]', 'Exécutions pipeline'),
        title: 'Exécutions',
        content:
          'L’historique des pipelines, les tiens ou ceux de tout le monde. Pratique pour retrouver un échec.',
      },
      // Last: the click opens the launch drawer, whose own guide takes over.
      todo(
        {
          target: byText('button', 'Lancer l'),
          title: 'Lancer un env',
          content:
            'Choisis ta branche et ton ticket : Backstage build, déploie, puis lance les E2E si tu veux. Une notification arrive quand l’env est prêt.',
        },
        { kind: 'click', hint: 'Clique sur « Lancer l’env »' },
      ),
    ],
  },
  {
    id: 'launch-pipeline',
    inDrawer: true,
    appliesTo: () => drawerOpen('Lancer un pipeline'),
    steps: [
      {
        target: field('Pipeline'),
        title: 'Le scénario',
        content:
          'Pipeline complet (build puis déploiement) dans la plupart des cas. Les autres rejouent une partie : déployer un build existant, relancer les migrations, lancer seulement les E2E.',
        placement: 'left',
      },
      todo(
        {
          target: field('Branche'),
          title: 'Ta branche',
          content:
            'La branche à déployer, par exemple feat/mp-1234-ocr. Le build part de son dernier commit.',
          placement: 'left',
        },
        { kind: 'fill', hint: 'Choisis ta branche' },
      ),
      todo(
        {
          target: field('Clé du ticket'),
          title: 'Le ticket',
          content:
            'Il donne son nom à l’env : MP-1234 devient mp-1234.feature.bobbee.co. L’URL s’affiche sous le champ.',
          placement: 'left',
        },
        { kind: 'fill', hint: 'Saisis la clé du ticket' },
      ),
      {
        target: field('Données'),
        title: 'Les données',
        content:
          'Dev pour une feature, staging pour un hotfix (*.hotfix.bobbee.co). Le choix est proposé d’après le type du ticket Jira.',
        placement: 'left',
      },
      {
        target: field('Tags Cypress'),
        title: 'Les tests E2E',
        content:
          'Coche les E2E pour lancer Cypress quand l’env est prêt. Les tags ajoutent des tests à ceux du domaine fonctionnel du ticket.',
        placement: 'left',
      },
      {
        target: field('Durée de vie'),
        title: 'Durée de vie',
        content:
          'L’env est supprimé à l’expiration. Tu pourras le prolonger depuis la liste ou depuis l’accueil.',
        placement: 'left',
      },
      {
        target: () =>
          findByText('[role="presentation"] button', 'Lancer') ??
          drawer('Lancer un pipeline')(),
        title: 'C’est parti',
        content:
          'Suis l’avancement dans la liste. La cloche te prévient quand l’env est prêt ou si le pipeline échoue.',
        placement: 'left',
      },
    ],
  },
  {
    id: 'env-control',
    inDrawer: true,
    appliesTo: () => drawerOpen('Contrôle de l'),
    steps: [
      {
        target: byText('[role="presentation"] label', 'Prolonger'),
        title: 'Prolonger ou raccourcir',
        content:
          'Ajoute jusqu’à 8 h, ou retire 1 h ou 4 h. Retirer 4 h à un env plus jeune que ça le supprime tout de suite.',
        placement: 'left',
      },
      {
        target: byText('[role="presentation"] label', 'Supprimer'),
        title: 'Supprimer',
        content:
          'Supprime l’env, sa base et ses données de test. Possible pour la personne qui l’a lancé depuis le portail, et pour les admins.',
        placement: 'left',
      },
    ],
  },
  {
    id: 'ticket-runs',
    appliesTo: runsOpen,
    // Shown on top of the runs drawer: it starts on its own only.
    inDrawer: true,
    steps: [
      {
        target: () =>
          findByText('h6', 'Environnement éphémère')?.parentElement ?? null,
        title: 'L’env du ticket',
        content:
          'URL de l’app, base de données, dernier déploiement et temps restant. Les boutons copient l’URL et l’adresse de la base.',
      },
      {
        target: byText('button', 'Rejouer la phase'),
        title: 'Relancer',
        content:
          'Rejoue une phase (déploiement, migrations, E2E) sans tout reconstruire. « Redéploiement complet » repart de zéro, une fois l’env supprimé.',
      },
      {
        target: byText('strong', 'Observabilité'),
        title: 'Logs et métriques',
        content:
          'Les liens CloudWatch de chaque lambda de cet env. Connecte-toi d’abord au compte AWS dev dans la console.',
      },
    ],
  },
  {
    id: 'my-work',
    appliesTo: pathname => /^\/my-work\/?$/.test(pathname) && noDrawer(),
    steps: [
      {
        target: card('À traiter maintenant'),
        title: 'À traiter maintenant',
        content:
          'Ce qui est cassé ou attend une action : pipelines en échec, PR à corriger, envs qui expirent. Vide, tout va bien.',
      },
      {
        target: '[aria-label="Filtrer les pull requests"]',
        title: 'Filtrer les PR',
        content:
          'Ne garde que les PR en conflit, à la CI rouge, ou celles à relire dont tu es code owner. Les filtres se cumulent et restent dans l’URL.',
      },
      {
        target: card('Mes tickets du sprint'),
        title: 'Tes tickets du sprint',
        content:
          'Une carte par ticket avec ses PR, son env et ses pipelines. Lance un env pour un ticket qui n’en a pas, ou prolonge celui qui existe.',
      },
      {
        target: card('À relire'),
        title: 'À relire',
        content:
          'Les PR qui attendent ta review, et celles qui ont changé depuis ta dernière relecture.',
      },
    ],
  },
  {
    id: 'releases',
    appliesTo: pathname =>
      pathname.startsWith('/delivery') && Boolean(card('Où est mon ticket')()),
    steps: [
      todo(
        {
          target: field('Clé Jira'),
          title: 'Où est mon ticket ?',
          content:
            'Tape le numéro du ticket (MP- est déjà là) : tu vois dans quels environnements (dev, staging, prod) ses commits sont déployés.',
        },
        { kind: 'fill', hint: 'Tape un numéro, par exemple 1234' },
      ),
      {
        target: card('Environnements'),
        title: 'Qui tourne où',
        content:
          'La version de chaque environnement, qui l’a déployée et quand. En dessous : ce que le prochain déploiement va livrer.',
      },
    ],
  },
  {
    id: 'productivity',
    appliesTo: pathname =>
      /^\/productivity\/lead-time\/?$/.test(pathname) &&
      Boolean(card('Lead time par mois')()),
    steps: [
      intro(
        'Productivité',
        <>
          <p>
            Le temps qu'il faut pour livrer : de l'ouverture du ticket Jira au
            merge dans develop.
          </p>
          <p>
            Par équipe, jamais par personne : aucun auteur de PR n'est stocké.
          </p>
        </>,
      ),
      {
        target: field('Équipe'),
        title: 'Les filtres',
        content:
          'Équipe (champ Team de Jira), type de ticket et période. Ils restent dans l’URL : copie-la pour partager la vue.',
      },
      {
        target: card('Derniers'),
        title: 'Les chiffres clés',
        content:
          'Lead time médian et p85 (la queue lente), comparés à la période précédente. Survole un libellé pour sa définition.',
      },
      {
        target: card('Lead time par mois'),
        title: 'La tendance',
        content:
          'La médiane et le p85 mois par mois. Compare un mois au précédent plutôt que les valeurs absolues.',
      },
      {
        target: card('Features mergées'),
        title: 'Les features',
        content:
          'Clique une clé pour sa fiche : chronologie, pull requests et runs CI de chacune.',
      },
      {
        target: card('Comment lire ces chiffres'),
        title: 'Comment lire ces chiffres',
        content:
          'Les définitions, les limites, et d’où viennent les données : la base du portail, synchronisée toutes les 15 min.',
      },
      // Last: the click opens the sprints tab, whose own guide takes over.
      todo(
        {
          target: byText('[role="tab"]', 'Sprints & équipes'),
          title: 'Sprints & équipes',
          content:
            'Les tickets traités par sprint, ce qu’ils ont demandé en pull requests et en runs CI, et chaque équipe sprint par sprint.',
        },
        { kind: 'click', hint: 'Clique sur « Sprints & équipes »' },
      ),
    ],
  },
  {
    id: 'productivity-sprints',
    appliesTo: pathname =>
      /^\/productivity\/sprints\/?$/.test(pathname) &&
      Boolean(card('Tickets traités par sprint')()),
    steps: [
      {
        target: card('Tickets traités par sprint'),
        title: 'Tickets traités par sprint',
        content:
          'Les tickets Jira terminés, par type. Clique une zone pour ses tickets, une colonne pour tout le sprint.',
      },
      {
        target: card('Tickets, pull requests et runs CI'),
        title: 'Pull requests et runs',
        content:
          'Les PR mergées dans develop qui citent les tickets du sprint, et leurs runs CI. En haut : PR par ticket, runs par PR et part d’échecs sur la période.',
      },
      {
        target: card('Par équipe'),
        title: 'Par équipe',
        content:
          'Chaque équipe sprint par sprint. Clique une équipe pour voir ses propres chiffres.',
      },
      {
        target: card('Comment lire ces chiffres'),
        title: 'Comment lire ces chiffres',
        content:
          'Ce qu’est un ticket traité, le hors sprint, et pourquoi des runs peuvent être « en cours de calcul ».',
      },
    ],
  },
  {
    id: 'productivity-feature',
    appliesTo: pathname =>
      /^\/productivity\/features\/[^/]+\/?$/.test(pathname) &&
      Boolean(card('Chronologie')()),
    steps: [
      {
        target: card('Chronologie'),
        title: 'Chronologie',
        content:
          'Ouverture du ticket, 1er commit, merges dans develop, avec le temps écoulé entre chaque étape.',
      },
      {
        // « Pull requests (2) »: the card, not the tile of the same name.
        target: card('Pull requests ('),
        title: 'Les pull requests',
        content:
          'Chaque PR mergée qui cite le ticket, avec ses runs CI et ses échecs. La tuile « Runs CI » en haut fait le total.',
      },
    ],
  },
  {
    id: 'create',
    // /create redirects to /create/templates.
    appliesTo: pathname => /^\/create(\/templates)?\/?$/.test(pathname),
    steps: [
      {
        ...intro(
          'Create',
          <>
            <p>
              Les templates génèrent du code standard pour app-bobbee : une
              lambda (câblée dans l'IaC, le CD et le catalogue) ou un package
              partagé.
            </p>
            <p>
              Crée d'abord ta branche de ticket (<code>feat/mp-1234-…</code>) :
              Backstage y ouvre une PR, tu relis et tu merges.
            </p>
          </>,
        ),
        data: { primaryLabel: 'Compris' },
      },
    ],
  },
];
